import { DOCUMENT, inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { AuthService } from './auth.service';
import { environment } from '../../environments/environment';

const WIDGET_SRC = 'https://js.serenitygpt.com/widget.js';
const CONTAINER_ID = 'serenity';
const TRIGGER_ID = 'serenity-trigger';
const SERENITY_API_URL = '/serenity/';

@Injectable({
  providedIn: 'root'
})
export class SerenityWidgetService {
  private readonly auth = inject(AuthService);
  private readonly doc = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);
  private loaded = false;
  private mounted = false;
  // Persistent, route-independent element the widget binds its open listener to.
  private trigger?: HTMLElement;

  // Injects the widget script and config without opening it. Safe to call repeatedly.
  load() {
    if (this.loaded || !isPlatformBrowser(this.platformId)) {
      return;
    }
    if (!SERENITY_API_URL || !this.auth.serenity_token) {
      console.warn('SerenityGPT widget is not configured (missing api url/token).');
      return;
    }
    this.loaded = true;

    const win = this.doc.defaultView as (Window & typeof globalThis) | null;
    if (win) {
      win.SERENITY_WIDGET = {
        api_url: SERENITY_API_URL,
        api_token: this.auth.serenity_token,
        el: CONTAINER_ID,
        history: true,
        popup: true,
        // Do not open on load; opening is driven by our own UI button.
        automount: false
      };
    }

    if (!this.doc.getElementById(CONTAINER_ID)) {
      const container = this.doc.createElement('div');
      container.id = CONTAINER_ID;
      container.style.zIndex = '2147483000';
      this.doc.body.appendChild(container);
    }

    // A hidden button in <body> that outlives Angular routes, so the widget's
    // open listener stays valid even when the page's own button is destroyed.
    this.trigger = (this.doc.getElementById(TRIGGER_ID) as HTMLElement | null) ?? undefined;
    if (!this.trigger) {
      const el = this.doc.createElement('button');
      el.id = TRIGGER_ID;
      el.type = 'button';
      el.style.display = 'none';
      el.setAttribute('aria-hidden', 'true');
      this.doc.body.appendChild(el);
      this.trigger = el;
    }

    const script = this.doc.createElement('script');
    // script.type = 'module';
    script.src = WIDGET_SRC;
    this.doc.body.appendChild(script);
  }

  // Opens the chat popup. Call from your own button's click handler; works across
  // route changes since the widget is anchored to a persistent hidden trigger.
  open() {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    this.load();

    // Once mounted the widget listens on the persistent trigger; re-firing it reopens.
    if (this.mounted) {
      this.trigger?.click();
      return;
    }
    this.mount();
  }

  private mount() {
    const win = this.doc.defaultView as (Window & typeof globalThis) | null;
    if (!win?.mountSerenityWidget) {
      return;
    }
    // First mount anchors to the persistent trigger and opens the popup immediately.
    win.mountSerenityWidget({ target: this.trigger });
    this.mounted = true;
  }
}
